// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import { Box, Input, UnstyledButton } from '@mantine/core';

import classes from 'components/Mantine/PillSelect.module.css';

export type PillSelectOption<T extends string> = {
  value: T;
  label: ReactNode;
  disabled?: boolean;
};

export type PillSelectProps<T extends string> = {
  value?: T | null;
  onChange: (value: T | null) => void;
  data: readonly PillSelectOption<T>[];
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  /**
   * When true, the pill labels use the monospace font, for values such as sizes and lengths.
   * @defaultValue false
   */
  mono?: boolean;
  /**
   * When true, pills wrap onto new rows, five to a row. When false, all pills share one row.
   * @defaultValue false
   */
  wrap?: boolean;
  /** Unused; accepted for `EditableCell` compatibility. */
  autoFocus?: boolean;
  /** Unused; accepted for `EditableCell` compatibility. */
  placeholder?: string;
  /** Called after a pill is picked. */
  onSubmit?: () => void;
};

/**
 * A single-select row of touch-sized (56px) pills, for picking one of a few options at a glance.
 * The picked pill is filled with the primary color. Use a select instead when there are more options than fit
 * in two rows.
 */
export const PillSelect = <T extends string>({
  value,
  onChange,
  data,
  label,
  description,
  error,
  required,
  disabled,
  mono = false,
  wrap = false,
  onSubmit,
}: PillSelectProps<T>) => (
  <Input.Wrapper label={label} description={description} error={error} required={required}>
    <Box
      className={classes.pills}
      role="radiogroup"
      aria-label={typeof label === 'string' ? label : undefined}
      data-wrap={wrap || undefined}
      data-mono={mono || undefined}
    >
      {data.map((option) => (
        <UnstyledButton
          key={option.value}
          className={classes.pill}
          role="radio"
          aria-checked={option.value === value}
          data-active={option.value === value || undefined}
          disabled={disabled || option.disabled}
          onClick={() => {
            if (option.value !== value) onChange(option.value);
            onSubmit?.();
          }}
        >
          {option.label}
        </UnstyledButton>
      ))}
    </Box>
  </Input.Wrapper>
);
